import { parseDate } from 'lib/date';
import { Classification, ClassificationOptions } from 'models/flexible/Classification';
import { Client, ClientOptions } from 'models/flexible/Client';
import { Group, GroupOptions } from 'models/flexible/Group';
import { GroupType, GroupTypeOptions } from 'models/flexible/GroupType';
import type { Typed } from 'services/api/fields/core';

import { EPISODE, PLAN_TYPE } from '../../constants/classifications';
export interface AIPromptOptions {
  groups?: GroupOptions[];
  groupTypes?: GroupTypeOptions[];
  client?: ClientOptions;
  planTypeClassifications?: ClassificationOptions[];
  episodeClassifications?: ClassificationOptions[];
  type?: string;
  config?: {
    explainer?: string;
    outputDefinition?: string;
    explainerLastUpdated?: string;
    outputDefinitionLastUpdated?: string;
  };
  publishedAt?: string | Date | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
  latestProcessPromptStatus?: string | null;
  [key: string]: any;
}

function getDefaults(): AIPromptOptions {
  return {
    id: '',
    name: '',
    displayName: '',
    description: '',
    type: '',
    clientId: '',
    clientIds: [],
    clientCount: 0,
    groupIds: [],
    groupCount: 0,
    config: {},
    groupTypeIds: [],
    planTypeClassificationIds: [],
    targetClassname: '',
    episodeClassificationIds: [],
    isPublished: false,
    publishedAt: null,
    createdAt: null,
    updatedAt: null,
    archived: false,
    latestProcessPromptStatus: null,
    groups: [],
    groupTypes: [],
    planTypeClassifications: [],
    episodeClassifications: [],
  };
}

// Forward declare so _AIPrompt's constructor can return the infused type

let AIPrompt: new <T extends AIPromptOptions>(options: T) => Typed<_AIPrompt<T>, T>;

class _AIPrompt<T extends AIPromptOptions = AIPromptOptions> {
  type!: string;
  config!: {
    explainer?: string;
    outputDefinition?: string;
    explainerLastUpdated?: Date;
    outputDefinitionLastUpdated?: Date;
  };
  publishedAt!: Date | null;
  createdAt!: Date | null;
  updatedAt!: Date | null;
  groups!: Group<NonNullable<T['groups']>[number]>[];
  groupTypes!: GroupType[];
  client!: Client<NonNullable<T['client']>> | undefined;
  latestProcessPromptStatus!: string | null;
  planTypeClassifications!: Classification<NonNullable<T['planTypeClassifications']>[number]>[];
  episodeClassifications!: Classification<NonNullable<T['episodeClassifications']>[number]>[];

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.type = opts.type?.includes('::') ? opts.type.split('::')[1] : (opts.type ?? '');
    this.config = {
      explainer: opts.config?.explainer ?? '',
      outputDefinition: opts.config?.outputDefinition ?? '',
      explainerLastUpdated: opts.config?.explainerLastUpdated
        ? (parseDate(opts.config.explainerLastUpdated) ?? undefined)
        : undefined,
      outputDefinitionLastUpdated: opts.config?.outputDefinitionLastUpdated
        ? (parseDate(opts.config.outputDefinitionLastUpdated) ?? undefined)
        : undefined,
    };
    this.publishedAt = parseDate(opts.publishedAt) ?? null;
    this.createdAt = parseDate(opts.createdAt) ?? null;
    this.updatedAt = parseDate(opts.updatedAt) ?? null;

    this.groups = (opts.groups ?? []).map((g) => new Group<NonNullable<T['groups']>[number]>(g));
    this.groupTypes = (opts.groupTypes ?? []).map((gt) => new GroupType(gt));
    this.client = opts.client ? new Client<NonNullable<T['client']>>(opts.client) : undefined;
    this.latestProcessPromptStatus = opts.latestProcessPromptStatus ?? null;
    this.planTypeClassifications = (opts.planTypeClassifications ?? []).map(
      (c) => new Classification<NonNullable<T['planTypeClassifications']>[number]>({ ...c, type: PLAN_TYPE })
    );
    this.episodeClassifications = (opts.episodeClassifications ?? []).map(
      (c) => new Classification<NonNullable<T['episodeClassifications']>[number]>({ ...c, type: EPISODE })
    );
  }

  get isProcessPromptComplete() {
    return this.latestProcessPromptStatus === null || this.latestProcessPromptStatus === 'completed';
  }
}

// eslint-disable-next-line prefer-const
AIPrompt = _AIPrompt as typeof AIPrompt;

export default AIPrompt;
export { AIPrompt };
export type AIPrompt<T extends AIPromptOptions = AIPromptOptions> = _AIPrompt<T> & Omit<T, keyof _AIPrompt<T>>;
